<template>
  <div class="plans-page">
    <!-- Background elements -->
    <div class="bg-grid"></div>
    <div class="bg-glow bg-glow-1"></div>

    <div class="plans-container">
      <div class="page-header">
        <div class="header-info">
          <h1>Subscription Plans</h1>
          <p class="subtitle">Manage your subscription plans and pricing</p>
        </div>
        <button class="btn btn-primary" @click="openCreateModal">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Create Plan
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-large"></div>
        <p>Loading plans...</p>
      </div>

      <!-- Error State -->
      <Transition name="fade">
        <div v-if="error" class="message error-message">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <span>{{ error }}</span>
        </div>
      </Transition>

      <!-- Empty State -->
      <div v-if="!loading && !error && plans && plans.length === 0" class="empty-state">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <line x1="3" y1="9" x2="21" y2="9"/>
          <line x1="9" y1="21" x2="9" y2="9"/>
        </svg>
        <p>No subscription plans found</p>
        <button class="btn btn-primary" @click="openCreateModal">Create your first plan</button>
      </div>

      <!-- Plans Table -->
      <div v-if="!loading && plans && plans.length" class="table-card">
        <div class="table-wrapper">
          <table class="plans-table">
            <thead>
              <tr>
                <th>Name</th>
                <th class="desktop-only">Description</th>
                <th>Status</th>
                <th>Inodes</th>
                <th>Sites</th>
                <th class="desktop-only">Price</th>
                <th class="desktop-only">Billing</th>
                <th class="desktop-only">Created</th>
                <th class="desktop-only">Updated</th>
                <th class="desktop-only actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="plan in plans" :key="plan.id" 
                  class="plan-row"
                  @click="handlePlanRowClick(plan, $event)">
                <td>
                  <span class="plan-name">{{ plan.name }}</span>
                </td>
                <td class="desktop-only">
                  <span v-if="plan.description" class="description">{{ plan.description }}</span>
                  <span v-else class="muted">—</span>
                </td>
                <td>
                  <span :class="['status-badge', plan.isActive ? 'active' : 'inactive']">
                    {{ plan.isActive ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <span v-if="plan.inodes !== null">{{ formatNumber(plan.inodes) }}</span>
                  <span v-else class="unlimited">∞</span>
                </td>
                <td>{{ plan.siteCount }}</td>
                <td class="desktop-only">${{ formatPrice(plan.basePrice) }}</td>
                <td class="desktop-only">
                  <span class="billing-badge">{{ formatBillingCycle(plan.billingCycle) }}</span>
                </td>
                <td class="desktop-only">
                  <div class="date-cell">
                    <span class="date">{{ formatDate(plan.createdAt) }}</span>
                    <span class="time">{{ formatTime(plan.createdAt) }}</span>
                  </div>
                </td>
                <td class="desktop-only">
                  <div class="date-cell">
                    <span class="date">{{ formatDate(plan.updatedAt) }}</span>
                    <span class="time">{{ formatTime(plan.updatedAt) }}</span>
                  </div>
                </td>
                <td class="desktop-only actions-col">
                  <div class="actions-wrapper">
                    <button
                      class="action-btn"
                      @click.stop="toggleMenu(plan.id)"
                      title="Actions">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="2"/>
                        <circle cx="12" cy="12" r="2"/>
                        <circle cx="12" cy="19" r="2"/>
                      </svg>
                    </button>
                    <Transition name="dropdown">
                      <div v-if="openMenuId === plan.id" class="dropdown-menu">
                        <button class="menu-item" @click.stop="showDiscounts(plan)">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                            <line x1="7" y1="7" x2="7.01" y2="7"/>
                          </svg>
                          Show discounts
                        </button>
                        <template v-if="!plan.isLocked">
                          <div class="menu-divider"></div>
                          <button class="menu-item" @click.stop="editPlan(plan)">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                            Edit
                          </button>
                          <button class="menu-item danger" @click.stop="confirmDeletePlan(plan)">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                            Delete
                          </button>
                        </template>
                      </div>
                    </Transition>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create Plan Modal -->
    <Transition name="modal">
      <div v-if="showCreateModal" class="modal-overlay" @click="closeCreateModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Create Subscription Plan</h3>
            <button class="close-button" @click="closeCreateModal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <form @submit.prevent="handleCreatePlan" class="modal-form">
            <div class="form-group">
              <label for="name">Plan Name</label>
              <input
                type="text"
                id="name"
                v-model="formData.name"
                required
                placeholder="e.g. Pro Plan"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="description">Description</label>
              <textarea
                id="description"
                v-model="formData.description"
                placeholder="Brief description of this plan"
                class="form-textarea"
                rows="2"
              ></textarea>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="basePrice">Base Price ($)</label>
                <input
                  type="number"
                  id="basePrice"
                  v-model.number="formData.basePrice"
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label for="billingCycle">Billing Cycle</label>
                <select
                  id="billingCycle"
                  v-model="formData.billingCycle"
                  required
                  class="form-select"
                >
                  <option value="">Select...</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="inodes">Inodes Limit</label>
                <input
                  type="number"
                  id="inodes"
                  v-model.number="formData.inodes"
                  min="0"
                  placeholder="Unlimited"
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label for="siteCount">Site Count</label>
                <input
                  type="number"
                  id="siteCount"
                  v-model.number="formData.siteCount"
                  required
                  min="0"
                  placeholder="1"
                  class="form-input"
                />
              </div>
            </div>
            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <div class="checkbox-wrapper">
                  <input
                    type="checkbox"
                    id="active"
                    v-model="formData.active"
                    class="form-checkbox"
                  />
                  <div class="checkbox-custom">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                </div>
                <span>Active Plan</span>
              </label>
            </div>
            <Transition name="fade">
              <div v-if="createError" class="message error-message small">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <span>{{ createError }}</span>
              </div>
            </Transition>
            <div class="modal-actions">
              <button type="button" @click="closeCreateModal" class="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" :disabled="creating" class="btn btn-primary">
                <span v-if="creating" class="spinner"></span>
                <span v-else>Create Plan</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>

    <!-- Plan Details Popup (Mobile) -->
    <Transition name="modal">
      <div v-if="showPlanDetails && selectedPlan" class="modal-overlay" @click="closePlanDetails">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>{{ selectedPlan.name }}</h3>
            <button class="close-button" @click="closePlanDetails">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <div class="detail-grid">
              <div class="detail-item">
                <label>Description</label>
                <span>{{ selectedPlan.description || '—' }}</span>
              </div>
              <div class="detail-item">
                <label>Status</label>
                <span :class="['status-badge', selectedPlan.isActive ? 'active' : 'inactive']">
                  {{ selectedPlan.isActive ? 'Active' : 'Inactive' }}
                </span>
              </div>
              <div class="detail-item">
                <label>Inodes</label>
                <span v-if="selectedPlan.inodes !== null">{{ formatNumber(selectedPlan.inodes) }}</span>
                <span v-else class="unlimited">Unlimited</span>
              </div>
              <div class="detail-item">
                <label>Site Count</label>
                <span>{{ selectedPlan.siteCount }}</span>
              </div>
              <div class="detail-item">
                <label>Base Price</label>
                <span>${{ formatPrice(selectedPlan.basePrice) }}</span>
              </div>
              <div class="detail-item">
                <label>Billing Cycle</label>
                <span>{{ formatBillingCycle(selectedPlan.billingCycle) }}</span>
              </div>
              <div class="detail-item">
                <label>Created</label>
                <span class="timestamp">{{ formatDate(selectedPlan.createdAt) }} {{ formatTime(selectedPlan.createdAt) }}</span>
              </div>
              <div class="detail-item">
                <label>Updated</label>
                <span class="timestamp">{{ formatDate(selectedPlan.updatedAt) }} {{ formatTime(selectedPlan.updatedAt) }}</span>
              </div>
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn btn-secondary" @click="closePlanDetails">Close</button>
            <button class="btn btn-primary" @click="editPlan(selectedPlan); closePlanDetails()">Edit</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete Confirmation Modal -->
    <Transition name="modal">
      <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
        <div class="modal-content delete-modal" @click.stop>
          <div class="modal-header">
            <h3>Delete Plan</h3>
            <button class="close-button" @click="closeDeleteModal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          <div class="modal-body center">
            <div class="delete-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <p>Are you sure you want to delete <strong>{{ planToDelete?.name }}</strong>?</p>
            <p class="warning-text">This action cannot be undone.</p>
            <Transition name="fade">
              <div v-if="deleteError" class="message error-message small">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <span>{{ deleteError }}</span>
              </div>
            </Transition>
          </div>
          <div class="modal-actions">
            <button type="button" @click="closeDeleteModal" class="btn btn-secondary" :disabled="deleting">
              Cancel
            </button>
            <button type="button" @click="handleDeletePlan" :disabled="deleting" class="btn btn-danger">
              <span v-if="deleting" class="spinner"></span>
              <span v-else>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Discounts Component -->
    <SubscriptionDiscounts
      :plan-id="selectedPlanForDiscounts?.id"
      :plan-name="selectedPlanForDiscounts?.name"
      :show="showDiscountsModal"
      @close="closeDiscountsModal"
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { api } from '../../utils/api'
import { useRouter } from 'vue-router'
import SubscriptionDiscounts from './SubscriptionDiscounts.vue'

export default {
  name: 'SubscriptionPlansView',
  components: {
    SubscriptionDiscounts
  },
  setup() {
    const router = useRouter()
    const plans = ref([])
    const loading = ref(true)
    const error = ref('')
    const showCreateModal = ref(false)
    const creating = ref(false)
    const createError = ref('')
    const showPlanDetails = ref(false)
    const selectedPlan = ref(null)
    const isMobile = ref(false)
    const showDeleteModal = ref(false)
    const planToDelete = ref(null)
    const deleting = ref(false)
    const deleteError = ref('')
    const openMenuId = ref(null)
    const formData = ref({
      name: '',
      description: '',
      active: true,
      inodes: null,
      siteCount: 1,
      basePrice: 0,
      billingCycle: ''
    })
    const showDiscountsModal = ref(false)
    const selectedPlanForDiscounts = ref(null)

    const fetchPlans = async () => {
      try {
        loading.value = true
        error.value = ''
        const data = await api('/admin/subscription-plans', {}, router)
        if (Array.isArray(data)) {
          plans.value = data
        } else {
          plans.value = []
        }
      } catch (err) {
        console.error('Failed to fetch plans:', err)
        error.value = err.code || 'Something went wrong please contact support'
      } finally {
        loading.value = false
      }
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const month = months[date.getMonth()]
      const day = date.getDate().toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${month} ${day}, ${year}`
    }

    const formatTime = (dateString) => {
      const date = new Date(dateString)
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const seconds = date.getSeconds().toString().padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    }

    const formatPrice = (price) => {
      return parseFloat(price).toFixed(2)
    }

    const formatNumber = (num) => {
      return num.toLocaleString('en-US')
    }

    const formatBillingCycle = (cycle) => {
      if (!cycle) return '—'
      return cycle.charAt(0).toUpperCase() + cycle.slice(1)
    }

    const openCreateModal = () => {
      showCreateModal.value = true
      createError.value = ''
      formData.value = {
        name: '',
        description: '',
        active: true,
        inodes: null,
        siteCount: 1,
        basePrice: 0,
        billingCycle: ''
      }
    }

    const closeCreateModal = () => {
      showCreateModal.value = false
      createError.value = ''
    }

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768
    }

    const handlePlanRowClick = (plan, event) => {
      if (event.target.closest('.actions-wrapper') || event.target.closest('.action-btn')) {
        return
      }
      
      if (isMobile.value) {
        openPlanDetails(plan)
      } else {
        showDiscounts(plan)
      }
    }

    const openPlanDetails = (plan) => {
      selectedPlan.value = plan
      showPlanDetails.value = true
    }

    const closePlanDetails = () => {
      showPlanDetails.value = false
      selectedPlan.value = null
    }

    const toggleMenu = (planId) => {
      if (openMenuId.value === planId) {
        openMenuId.value = null
      } else {
        openMenuId.value = planId
      }
    }

    const closeMenu = () => {
      openMenuId.value = null
    }

    const editPlan = (plan) => {
      closeMenu()
      router.push(`/admin/subscription-plans/${plan.id}/edit`)
    }

    const showDiscounts = (plan) => {
      closeMenu()
      selectedPlanForDiscounts.value = plan
      showDiscountsModal.value = true
    }

    const closeDiscountsModal = () => {
      showDiscountsModal.value = false
      selectedPlanForDiscounts.value = null
    }

    const confirmDeletePlan = (plan) => {
      closeMenu()
      planToDelete.value = plan
      showDeleteModal.value = true
      deleteError.value = ''
    }

    const closeDeleteModal = () => {
      showDeleteModal.value = false
      planToDelete.value = null
      deleteError.value = ''
    }

    const handleDeletePlan = async () => {
      if (!planToDelete.value) return

      deleting.value = true
      deleteError.value = ''

      try {
        await api(`/admin/subscription-plans/${planToDelete.value.id}`, {
          method: 'DELETE'
        }, router)

        closeDeleteModal()
        await fetchPlans()
      } catch (err) {
        console.error('Failed to delete plan:', err)
        deleteError.value = err.code || 'Something went wrong please contact support'
      } finally {
        deleting.value = false
      }
    }

    const handleCreatePlan = async () => {
      creating.value = true
      createError.value = ''

      try {
        await api('/admin/subscription-plans', {
          method: 'POST',
          body: JSON.stringify({
            name: formData.value.name,
            description: formData.value.description,
            isActive: formData.value.active,
            inodes: formData.value.inodes || null,
            siteCount: formData.value.siteCount,
            basePrice: formData.value.basePrice,
            billingCycle: formData.value.billingCycle
          })
        }, router)

        closeCreateModal()
        await fetchPlans()
      } catch (err) {
        console.error('Failed to create plan:', err)
        createError.value = err.code || 'Something went wrong please contact support'
      } finally {
        creating.value = false
      }
    }

    const handleClickOutside = (event) => {
      if (!event.target.closest('.actions-wrapper')) {
        closeMenu()
      }
    }

    onMounted(() => {
      checkMobile()
      window.addEventListener('resize', checkMobile)
      document.addEventListener('click', handleClickOutside)
      fetchPlans()
    })

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile)
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      plans,
      loading,
      error,
      showCreateModal,
      creating,
      createError,
      formData,
      showPlanDetails,
      selectedPlan,
      isMobile,
      showDeleteModal,
      planToDelete,
      deleting,
      deleteError,
      openCreateModal,
      closeCreateModal,
      handleCreatePlan,
      editPlan,
      handlePlanRowClick,
      openPlanDetails,
      closePlanDetails,
      confirmDeletePlan,
      closeDeleteModal,
      handleDeletePlan,
      openMenuId,
      toggleMenu,
      closeMenu,
      showDiscounts,
      showDiscountsModal,
      selectedPlanForDiscounts,
      closeDiscountsModal,
      formatDate,
      formatTime,
      formatPrice,
      formatNumber,
      formatBillingCycle
    }
  }
}
</script>

<style scoped>
.plans-page {
  min-height: calc(100vh - 40px);
  position: relative;
  padding: 32px 24px;
}

/* Background elements */
.bg-grid {
  position: fixed;
  inset: 0;
  background-image: 
    linear-gradient(rgba(60, 98, 230, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(60, 98, 230, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.25;
  pointer-events: none;
  z-index: 0;
}

.bg-glow-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(60, 98, 230, 0.2) 0%, transparent 70%);
  top: -150px;
  right: 5%;
}

.plans-container {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Page header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
  gap: 20px;
  flex-wrap: wrap;
}

.header-info h1 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.subtitle {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0;
}

/* Loading & Empty states */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
}

.loading-state p,
.empty-state p {
  margin: 16px 0;
  font-size: 1rem;
}

.empty-state svg {
  width: 64px;
  height: 64px;
  color: var(--text-muted);
}

.empty-state .btn {
  margin-top: 8px;
}

.spinner-large {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(60, 98, 230, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Messages */
.message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-size: 0.9rem;
}

.message svg {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
}

.message.small {
  padding: 12px 14px;
  font-size: 0.85rem;
  margin-bottom: 16px;
}

.error-message {
  background: rgba(221, 82, 76, 0.1);
  border: 1px solid rgba(221, 82, 76, 0.2);
  color: var(--error-color);
}

/* Table Card */
.table-card {
  background: rgba(24, 24, 25, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 
    0 20px 40px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
}

.plans-table {
  width: 100%;
  border-collapse: collapse;
}

.plans-table th,
.plans-table td {
  padding: 14px 16px;
  text-align: left;
}

.plans-table th {
  background: rgba(61, 61, 61, 0.3);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.plans-table td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 0.9rem;
  color: var(--text-primary);
}

.plan-row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.plan-row:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.plan-row:last-child td {
  border-bottom: none;
}

.plan-name {
  font-weight: 500;
  color: var(--text-primary);
}

.description {
  color: var(--text-secondary);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muted {
  color: var(--text-muted);
}

.unlimited {
  color: var(--accent-color);
  font-weight: 600;
  font-size: 1.1rem;
}

/* Badges */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.status-badge.active {
  background: rgba(94, 194, 105, 0.15);
  color: var(--success-color);
}

.status-badge.inactive {
  background: rgba(128, 128, 128, 0.15);
  color: var(--text-muted);
}

.billing-badge {
  display: inline-block;
  padding: 4px 10px;
  background: rgba(60, 98, 230, 0.1);
  color: var(--accent-color);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
}

.date-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.date-cell .date {
  font-size: 0.85rem;
  color: var(--text-primary);
}

.date-cell .time {
  font-size: 0.75rem;
  color: var(--text-muted);
  font-family: 'IBM Plex Mono', monospace;
}

.actions-col {
  width: 60px;
  text-align: center !important;
}

/* Actions */
.actions-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn svg {
  width: 18px;
  height: 18px;
}

.action-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: var(--text-primary);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: rgba(24, 24, 25, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  text-align: left;
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  font-size: 0.9rem;
  font-family: inherit;
  transition: background-color 0.15s ease;
}

.menu-item svg {
  width: 16px;
  height: 16px;
  color: var(--text-secondary);
}

.menu-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.menu-item.danger {
  color: var(--error-color);
}

.menu-item.danger svg {
  color: var(--error-color);
}

.menu-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 4px 0;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.2s ease;
}

.btn svg {
  width: 16px;
  height: 16px;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(60, 98, 230, 0.25);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(60, 98, 230, 0.35);
}

.btn-secondary {
  background-color: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.btn-danger {
  background: linear-gradient(135deg, var(--error-color) 0%, #a83232 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(221, 82, 76, 0.25);
}

.btn-danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(221, 82, 76, 0.35);
}

/* Spinner */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 20px;
}

.modal-content {
  background: rgba(24, 24, 25, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.close-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-muted);
  transition: color 0.2s;
  display: flex;
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.close-button:hover {
  color: var(--text-primary);
}

.modal-form {
  padding: 24px;
}

.modal-body {
  padding: 24px;
}

.modal-body.center {
  text-align: center;
}

.modal-body p {
  margin: 0 0 8px;
  color: var(--text-primary);
}

.warning-text {
  color: var(--text-muted) !important;
  font-size: 0.9rem;
}

.delete-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  background: rgba(221, 82, 76, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-icon svg {
  width: 28px;
  height: 28px;
  color: var(--error-color);
}

.modal-actions {
  padding: 20px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

/* Form elements */
.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-size: 0.95rem;
  font-family: inherit;
  background-color: rgba(61, 61, 61, 0.3);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.form-input::placeholder,
.form-textarea::placeholder {
  color: var(--text-muted);
}

.form-input:hover,
.form-select:hover,
.form-textarea:hover {
  border-color: rgba(255, 255, 255, 0.15);
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(60, 98, 230, 0.15);
}

.form-textarea {
  resize: vertical;
  min-height: 60px;
}

/* Checkbox */
.checkbox-group {
  margin-bottom: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.checkbox-wrapper {
  position: relative;
  flex-shrink: 0;
}

.form-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: rgba(61, 61, 61, 0.3);
}

.checkbox-custom svg {
  width: 12px;
  height: 12px;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.2s ease;
  color: white;
}

.form-checkbox:checked + .checkbox-custom {
  background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
  border-color: var(--accent-color);
}

.form-checkbox:checked + .checkbox-custom svg {
  opacity: 1;
  transform: scale(1);
}

/* Detail grid */
.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item label {
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin: 0;
}

.detail-item span {
  color: var(--text-primary);
  font-size: 0.9rem;
}

.detail-item .timestamp {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.8rem;
  color: var(--text-secondary);
}

/* Desktop only */
.desktop-only {
  display: table-cell;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.95);
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Responsive */
@media (max-width: 768px) {
  .plans-page {
    padding: 20px 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .page-header .btn {
    width: 100%;
    justify-content: center;
  }

  .desktop-only {
    display: none !important;
  }

  .plans-table th,
  .plans-table td {
    padding: 12px;
    font-size: 0.85rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }

  .modal-actions .btn {
    width: 100%;
  }
}
</style>
